<template>
  <div>
    <div class="gd-section">
      <div class="gd-section-head">
        <span class="n">§1</span>
        <span class="t">Identificação da guia</span>
        <span class="d">
          {{ identificationHint }}
          <template v-if="tissVersion"> · versão TISS <b>{{ tissVersion }}</b></template>
        </span>
      </div>
      <div class="gd-section-body">
        <div class="columns" v-if="readonly">
          <div class="column col-3 col-sm-6">
            <guide-read-field :label="labels.startDateShort" :value="startDateText"/>
          </div>
          <div class="column col-3 col-sm-6">
            <guide-read-field :label="labels.providerShort" :value="form.referral.provider"/>
          </div>
          <div class="column col-3 col-sm-6">
            <guide-read-field :label="insuranceReferralLabel" :value="form.referral.insurance"/>
          </div>
          <div class="column col-3 col-sm-6" v-if="type === 'sadt'">
            <guide-read-field :label="labels.main" :value="form.referral.main"/>
          </div>
          <div class="column col-3 col-sm-6" v-if="isConsultation">
            <guide-read-field label="Senha" :value="form.authorization.password"/>
          </div>
        </div>
        <div class="columns" v-else>
          <div class="column col-md-6 col-sm-12 form-group"
               :class="[isConsultation ? 'col-2' : 'col-3',
                        {'has-error': err('startDate')}]">
            <label class="form-label">{{ labels.startDate }}</label>
            <dx-input-date :id="fieldId('startDate')" class="form-input"
                           v-model="form.startDate" @blur="touch('startDate')"/>
            <div class="form-input-hint" v-if="err('startDate')">{{ hint('startDate') }}</div>
          </div>
          <div class="column col-2 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('startTime')}">
            <label class="form-label">Hora</label>
            <input type="text" class="form-input" :id="fieldId('startTime')"
                   v-model="form.startTime" v-mask="'00:00'" placeholder="00:00"
                   @blur="touch('startTime')">
            <div class="form-input-hint" v-if="err('startTime')">{{ hint('startTime') }}</div>
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('referral.provider')}">
            <label class="form-label">
              {{ labels.provider }}
              <small v-if="providerMode === 'password'">copia a senha</small>
              <small v-else-if="providerMode === 'insurance_referral'">copia a operadora</small>
            </label>
            <input type="text" class="form-input gd-provider-input"
                   :class="{locked: providerLocked}"
                   :id="fieldId('referral.provider')"
                   v-model="form.referral.provider"
                   :readonly="providerLocked"
                   :placeholder="providerPlaceholder"
                   @blur="touch('referral.provider')">
            <div class="form-input-hint" v-if="err('referral.provider')">
              {{ hint('referral.provider') }}
            </div>
          </div>
          <div class="column col-md-6 col-sm-12 form-group"
               :class="[isConsultation ? 'col-3' : 'col-4',
                        {'has-error': err('referral.insurance')}]">
            <label class="form-label">
              {{ insuranceReferralLabel }}
              <small v-if="!isRequired('referral.insurance')">opcional</small>
            </label>
            <input type="text" class="form-input" :id="fieldId('referral.insurance')"
                   v-model="form.referral.insurance" @blur="touch('referral.insurance')"
                   @input="$emit('insurance-referral')">
            <div class="form-input-hint" v-if="err('referral.insurance')">
              {{ hint('referral.insurance') }}
            </div>
          </div>
          <div class="column col-2 col-md-6 col-sm-12 form-group" v-if="isConsultation">
            <label class="form-label">Senha <small>opcional</small></label>
            <input type="text" class="form-input" :id="fieldId('authorization.password')"
                   v-model="form.authorization.password" placeholder="Senha da operadora"
                   @input="$emit('password')">
          </div>
          <div class="column col-4 col-md-6 col-sm-12 form-group" v-if="type === 'sadt'"
               :class="{'has-error': err('referral.main')}">
            <label class="form-label">
              {{ labels.main }}
              <small v-if="!isRequired('referral.main')">opcional neste plano</small>
            </label>
            <input type="text" class="form-input" :id="fieldId('referral.main')"
                   v-model="form.referral.main" @blur="touch('referral.main')">
            <div class="form-input-hint" v-if="err('referral.main')">
              {{ hint('referral.main') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="gd-section">
      <div class="gd-section-head">
        <span class="n">§2</span>
        <span class="t">Beneficiário</span>
        <span class="d">{{ beneficiaryHint }}</span>
      </div>
      <div class="gd-section-body">
        <div class="columns" v-if="readonly">
          <div class="column col-4 col-sm-6">
            <guide-read-field :label="labels.record" :value="form.insured.record"/>
          </div>
          <div class="column col-3 col-sm-6">
            <guide-read-field :label="labels.validity" :value="validityText"/>
          </div>
          <div class="column col-5 col-sm-12">
            <guide-read-field :label="labels.name" :value="form.insured.name"/>
          </div>
          <template v-if="type !== 'simple'">
            <div class="column col-4 col-sm-6">
              <guide-read-field :label="labels.cns" :value="form.patient.cns"/>
            </div>
            <div class="column col-5 col-sm-6">
              <guide-read-field :label="labels.socialName" :value="form.patient.socialName"/>
            </div>
            <div class="column col-3 col-sm-12">
              <guide-read-field :label="labels.newborn"
                                :value="form.patient.isNewborn ? 'Sim' : 'Não'"/>
            </div>
          </template>
        </div>
        <div class="columns" v-else>
          <div class="column col-4 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('insured.record')}">
            <label class="form-label">{{ labels.record }}</label>
            <input type="text" class="form-input" :id="fieldId('insured.record')"
                   v-model="form.insured.record" :maxlength="recordMaxLength"
                   @blur="touch('insured.record')">
            <div class="form-input-hint" v-if="err('insured.record')">
              {{ hint('insured.record') }}
            </div>
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('insured.validity')}">
            <label class="form-label">{{ labels.validity }}</label>
            <dx-input-date :id="fieldId('insured.validity')" class="form-input"
                           v-model="form.insured.validity" @blur="touch('insured.validity')"/>
            <div class="form-input-hint" v-if="err('insured.validity')">
              {{ hint('insured.validity') }}
            </div>
          </div>
          <div class="column col-5 col-md-12 col-sm-12 form-group"
               :class="{'has-error': err('insured.name')}">
            <label class="form-label">{{ labels.name }}</label>
            <input type="text" class="form-input" :id="fieldId('insured.name')"
                   v-model="form.insured.name" @blur="touch('insured.name')">
            <div class="form-input-hint" v-if="err('insured.name')">
              {{ hint('insured.name') }}
            </div>
          </div>
          <template v-if="type !== 'simple'">
            <div class="column col-4 col-md-6 col-sm-12 form-group">
              <label class="form-label">{{ labels.cns }}</label>
              <input type="text" class="form-input" v-model="form.patient.cns"
                     v-mask="'000 0000 0000 0000'">
            </div>
            <div class="column col-5 col-md-6 col-sm-12 form-group">
              <label class="form-label">{{ labels.socialName }}</label>
              <input type="text" class="form-input" v-model="form.patient.socialName">
            </div>
            <div class="column col-3 col-md-12 col-sm-12 form-group gd-switch-column">
              <label class="form-label">&nbsp;</label>
              <label class="form-switch">
                <input type="checkbox" v-model="form.patient.isNewborn">
                <i class="form-icon"></i>{{ labels.newborn }}
              </label>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import fieldMixin from '../fieldMixin';
import GuideReadField from './GuideReadField.vue';

export default {
  mixins: [fieldMixin],
  components: {
    GuideReadField,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    planSettings: {
      type: Object,
      default: () => ({}),
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isRequired(path) {
      const node = this.field(path);
      return !!(node && node.$params && node.$params.required);
    },
  },
  computed: {
    isConsultation() {
      return this.type === 'consultation';
    },
    labels() {
      if (this.isConsultation) {
        return {
          startDate: '18 · Data',
          startDateShort: '18 · Atendimento',
          provider: '2 · Nº guia prestador',
          providerShort: '2 · Nº guia prestador',
          record: '4 · Nº da carteirinha',
          validity: '5 · Validade',
          newborn: '6 · Atendimento a RN',
          name: '7 · Nome do beneficiário',
          cns: '8 · CNS',
          socialName: '26 · Nome social',
        };
      }
      if (this.type === 'simple') {
        return {
          startDate: 'Data do atendimento',
          startDateShort: 'Atendimento',
          provider: 'Nº da guia',
          providerShort: 'Nº da guia',
          record: 'Nº da carteirinha',
          validity: 'Validade',
          newborn: 'Atendimento a RN',
          name: 'Nome do beneficiário',
          cns: 'CNS',
          socialName: 'Nome social',
        };
      }
      return {
        startDate: 'Data do atendimento',
        startDateShort: 'Atendimento',
        provider: '2 · Nº guia prestador',
        providerShort: '2 · Nº guia prestador',
        main: '3 · Nº guia principal',
        record: '8 · Nº da carteirinha',
        validity: '9 · Validade',
        newborn: '12 · Atendimento a RN',
        name: '10 · Nome do beneficiário',
        cns: '11 · CNS',
        socialName: '89 · Nome social',
      };
    },
    providerMode() {
      const general = this.planSettings.general || {};
      return general.providerReferral && general.providerReferral.mode
        ? general.providerReferral.mode
        : 'manual';
    },
    providerLocked() {
      return (this.providerMode === 'automatic' && this.isNew)
        || this.providerMode === 'password'
        || this.providerMode === 'insurance_referral';
    },
    providerPlaceholder() {
      if (this.providerMode === 'automatic' && this.isNew) return '[ Gerado ao salvar ]';
      if (this.providerMode === 'password') return '[ Preenchido pela senha ]';
      if (this.providerMode === 'insurance_referral') return '[ Preenchido pelo nº da operadora ]';
      return '';
    },
    insuranceReferralLabel() {
      if (this.type === 'consultation') return '3 · Nº guia operadora';
      if (this.type === 'sadt') return '7 · Nº guia operadora';
      return 'Nº guia operadora';
    },
    tissVersion() {
      const version = this.planSettings.tiss && this.planSettings.tiss.version
        ? String(this.planSettings.tiss.version)
        : '';
      if (!version) return null;
      const digits = version.replace(/\D/g, '');
      return digits.length === 5
        ? `${digits[0]}.${digits.slice(1, 3)}.${digits.slice(3)}`
        : version;
    },
    identificationHint() {
      const parts = [];
      if (this.providerMode === 'automatic') parts.push('Numeração automática pelo plano');
      else if (this.providerMode === 'password') parts.push('Nº do prestador copia a senha');
      else if (this.providerMode === 'insurance_referral') parts.push('Nº do prestador copia a operadora');
      else parts.push('Numeração manual');
      return parts.join(' · ');
    },
    record() {
      const general = this.planSettings.general || {};
      return general.record || {};
    },
    recordMaxLength() {
      return this.record.digits && this.record.digits.enabled && this.record.digits.max
        ? this.record.digits.max
        : 20;
    },
    beneficiaryHint() {
      if (this.record.required && this.record.requiredValidity) {
        return 'Carteirinha obrigatória com validade neste plano';
      }
      if (this.record.required) return 'Carteirinha obrigatória neste plano';
      if (this.type === 'simple') return 'Carteirinha do paciente neste convênio';
      if (this.isConsultation) return 'Campos 4–8 e 26 da guia de consulta';
      return 'Campos 8–12 e 89 da guia SP/SADT';
    },
    startDateText() {
      if (!this.form.startDate) return '';
      const date = moment(this.form.startDate).format('DD/MM/YYYY');
      return this.form.startTime ? `${date} ${this.form.startTime}` : date;
    },
    validityText() {
      return this.form.insured.validity
        ? moment(this.form.insured.validity).format('DD/MM/YYYY')
        : '';
    },
  },
};
</script>
